import { gql } from '@apollo/client';
import { addMocksToSchema } from '@graphql-tools/mock';
import { makeExecutableSchema } from '@graphql-tools/schema';
import {
  GraphQLSchema,
  InterfaceTypeDefinitionNode,
  NamedTypeNode,
  ObjectTypeDefinitionNode,
  OperationDefinitionNode,
  UnionTypeDefinitionNode,
  assertSchema,
  buildASTSchema,
  graphql,
  parse,
  printSchema,
  visit,
} from 'graphql';
import { mapValues } from 'lodash';
import { uniqueId } from 'lodash/fp';
import * as monacoEditor from 'monaco-editor';

import { isTypeDefinition } from 'src/app/graph/documentationPage/schemaPage/schemaFilter/filterGraphQLHelpers';
import {
  FieldShape,
  getVariableShapeForReference,
} from 'src/app/graph/explorerPage/helpers/getVariableShapes/getVariableShapes';
import { getPathToOffset } from 'src/app/graph/explorerPage/helpers/graphqlParseHelpers';
import { JSONValue } from 'src/app/graph/explorerPage/helpers/types';
import { getOrSetRecord } from 'src/lib/getOrSetRecord';
import { unwrapType } from 'src/lib/graphql/graphqlHelpers';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { operationPathToGraphQLPath } from '../../helpers/operationPathHelpers';

import { ResponseAndMetrics } from './performOperation/performOperation';

function mockFieldShape(fieldShape: FieldShape): JSONValue {
  if (!fieldShape.nonNull) {
    return null;
  }
  switch (fieldShape.kind) {
    case 'array':
      return [];
    case 'boolean':
      return true;
    case 'number':
      return 0;
    case 'string':
      return '';
    case 'enum':
      // TODO: Remove non null assertion, added when turning on noUncheckedIndexedAccess
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return fieldShape.enumValues[0]!;
    case 'object':
      return mapValues(fieldShape.fields, mockFieldShape);
    default:
      return null;
  }
}

/**
 *
 * @returns a modified schema based on an operation string, so that any mocked response will
 * include data for the requested fields. Without this function, a user might
 * request certain fragments on a union or interface and the mock will return
 * data for a type not included in the selection set, so they see no data. A user
 * expects data to come back on their requests, but if they are only including
 * 1 of 7 types in the union, data will only come back 1/7th of the time.
 *
 * In the schema returned by this function:
 *
 * 1. Fields of Union type that are requested point to a new mocked type that
 *    only includes the requested types in the users selection set
 * 2. Fields of Interface type that are requested point to a new mocked type that
 *    looks exactly the same as the OG interface, but is named differently.
 *    All requested types will implement this new interface in addition to the og.
 */

const modifyMockedSchemaUnionsBasedOnOperation = ({
  schema,
  operationString,
  model,
}: {
  operationString: string;
  schema: GraphQLSchema;
  model: monacoEditor.editor.ITextModel | null;
}): GraphQLSchema => {
  if (!model) return schema;
  const operationDocumentNode = parse(operationString);

  /**
   * Construct a map of [parentTypeName.fieldName] of union or interface types mapped
   * to the set of the types that are requested in the operationString, and the
   * original ast node for the type def of the requested field.
   * */
  const fieldsToUnionOrInterfaceTypesToInclude: Record<
    string,
    {
      originalFieldTypeNode:
        | UnionTypeDefinitionNode
        | InterfaceTypeDefinitionNode;
      typesToInclude: string[];
    }
  > = {};

  visit(operationDocumentNode, {
    SelectionSet: {
      enter(node) {
        node.selections.forEach((selection) => {
          if (
            selection.kind === 'Field' &&
            isMinLengthArray(0, selection.selectionSet?.selections)
          ) {
            // grab the name of the field, not the potentially aliased name
            const selectionNameLocation = selection.name.loc;
            if (!selectionNameLocation) return;

            const pathToField = getPathToOffset(
              model,
              new monacoEditor.Position(
                selectionNameLocation.startToken.line,
                selectionNameLocation.startToken.column,
              ),
            );

            if (!pathToField) {
              // No path, do nothing
              return null;
            }

            const { path } = pathToField;

            // Needs full path to find field, use monacoUriToDocNodes or something
            // similar
            const docPath = path && operationPathToGraphQLPath(path, schema);
            const [parent, field] = docPath?.slice(-2) ?? [];

            if (!field || !parent) {
              return null;
            }
            const parentType = unwrapType(parent);
            const fieldType = unwrapType(field);
            selection.selectionSet?.selections.forEach(
              (potentialInlineSelection) => {
                if (
                  potentialInlineSelection.kind === 'InlineFragment' &&
                  'type' in field &&
                  'name' in fieldType &&
                  fieldType.astNode &&
                  (fieldType.astNode.kind === 'UnionTypeDefinition' ||
                    fieldType.astNode.kind === 'InterfaceTypeDefinition') &&
                  parentType.astNode
                ) {
                  const inlineFragment = potentialInlineSelection;
                  const value = getOrSetRecord(
                    `${parentType.astNode.name.value}.${field.name}`,
                    {
                      originalFieldTypeNode: fieldType.astNode,
                      typesToInclude: [],
                    },
                    fieldsToUnionOrInterfaceTypesToInclude,
                  );
                  if (
                    inlineFragment.typeCondition &&
                    !value.typesToInclude.includes(
                      inlineFragment.typeCondition?.name.value,
                    )
                  )
                    value.typesToInclude.push(
                      inlineFragment.typeCondition?.name.value,
                    );
                }
              },
            );
          }
        });
      },
    },
  });

  if (!Object.keys(fieldsToUnionOrInterfaceTypesToInclude)) {
    return schema;
  }
  const originalSchemaDocumentNode = parse(printSchema(schema));
  let newSchemaDocumentDefinitions = [
    ...originalSchemaDocumentNode.definitions,
  ];

  Object.entries(fieldsToUnionOrInterfaceTypesToInclude).forEach(
    ([fieldAndParentType, { originalFieldTypeNode, typesToInclude }]) => {
      const [parentTypeName, fieldName] = fieldAndParentType.split('.');
      const parentTypeNodeToModify = newSchemaDocumentDefinitions.find(
        (
          definition,
        ): definition is Extract<
          typeof definition,
          ObjectTypeDefinitionNode | InterfaceTypeDefinitionNode
        > =>
          !!(
            isTypeDefinition(definition) &&
            definition.name.value === parentTypeName &&
            'fields' in definition &&
            definition.fields?.some((field) => field.name.value === fieldName)
          ),
      );
      const fieldDefinitionNode = parentTypeNodeToModify?.fields?.find(
        (field) => field.name.value === fieldName,
      );

      if (
        !parentTypeNodeToModify ||
        !parentTypeNodeToModify.fields ||
        !fieldDefinitionNode
      )
        return;

      /**
       * If this user is querying an interface field,
       * and is querying on the interface type name rather than a type
       * implementing that interface, don't modify the schema.
       * The existing mock will always give some data, since the interface type
       * will always be returned.
       */
      if (
        originalFieldTypeNode.kind === 'InterfaceTypeDefinition' &&
        typesToInclude.includes(originalFieldTypeNode.name.value)
      ) {
        return;
      }

      const newFields = parentTypeNodeToModify.fields.filter(
        (field) => field.name.value !== fieldName,
      );
      const mockedNamedType: NamedTypeNode = {
        kind: 'NamedType',
        name: {
          kind: 'Name',
          value: uniqueId(`MockedGeneratedTypeForField${fieldName}`),
        },
      };
      newFields.push({
        ...fieldDefinitionNode,
        type: mockedNamedType,
      });
      const newParentTypeDocumentNode = {
        ...parentTypeNodeToModify,
        fields: newFields,
      };

      /**
       * Replace the parent type fields with the modified field pointing
       * to the new mocked type
       */
      newSchemaDocumentDefinitions = newSchemaDocumentDefinitions.filter(
        (def) =>
          !(
            def.kind === newParentTypeDocumentNode.kind &&
            def.name.value === newParentTypeDocumentNode.name.value
          ),
      );
      newSchemaDocumentDefinitions.push(newParentTypeDocumentNode);

      const mockedTypeNode:
        | UnionTypeDefinitionNode
        | InterfaceTypeDefinitionNode = {
        ...originalFieldTypeNode,
        name: mockedNamedType.name,
        ...(originalFieldTypeNode.kind === 'UnionTypeDefinition'
          ? {
              types: typesToInclude.map((type) => ({
                kind: 'NamedType',
                name: { kind: 'Name', value: type },
              })),
            }
          : {}),
      };
      newSchemaDocumentDefinitions.push(mockedTypeNode);

      /**
       * For interfaces, we need to have the types in the fragments in this selection
       * set implement the new interface we just added to the schema
       */
      if (originalFieldTypeNode.kind === 'InterfaceTypeDefinition') {
        typesToInclude.forEach((type) => {
          const typeNodeToModify = newSchemaDocumentDefinitions.find(
            (
              definition,
            ): definition is Extract<
              typeof definition,
              ObjectTypeDefinitionNode | InterfaceTypeDefinitionNode
            > =>
              isTypeDefinition(definition) &&
              'name' in definition &&
              'interfaces' in definition &&
              definition.name?.value === type,
          );
          if (typeNodeToModify) {
            const newType = {
              ...typeNodeToModify,
              interfaces: [
                ...(typeNodeToModify.interfaces
                  ? typeNodeToModify.interfaces.filter(
                      (interfaces) =>
                        interfaces.name.value !==
                        originalFieldTypeNode.name.value,
                    )
                  : []),
                mockedNamedType,
              ],
            };
            newSchemaDocumentDefinitions = newSchemaDocumentDefinitions.filter(
              (def) =>
                !('name' in def && def.name?.value === newType.name.value),
            );
            newSchemaDocumentDefinitions.push(newType);
          }
        });
      }
    },
  );

  return buildASTSchema({
    kind: originalSchemaDocumentNode.kind,
    loc: originalSchemaDocumentNode.loc,
    definitions: newSchemaDocumentDefinitions,
  });
};

export const performMockOperation = async ({
  operationString,
  operationName,
  schema: unmodifiedSchema,
  editor,
}: {
  operationString: string;
  operationName?: string;
  schema: GraphQLSchema;
  editor: monacoEditor.editor.IStandaloneCodeEditor;
}): Promise<ResponseAndMetrics> => {
  const schema = modifyMockedSchemaUnionsBasedOnOperation({
    schema: unmodifiedSchema,
    operationString,
    model: editor.getModel(),
  });

  const startTime = Date.now();
  const executableSchema = makeExecutableSchema({
    typeDefs: printSchema(schema),
  });

  const schemaWithMocks = assertSchema(
    addMocksToSchema({ schema: executableSchema }),
  );
  const variableDefinitions = gql(operationString).definitions.find(
    (definition): definition is OperationDefinitionNode =>
      definition.kind === 'OperationDefinition',
  )?.variableDefinitions;
  const variableValues = Object.fromEntries(
    (variableDefinitions ?? []).map((definition) => [
      definition.variable.name.value,
      mockFieldShape(getVariableShapeForReference(definition.type, schema)),
    ]),
  );
  const result = await graphql(
    schemaWithMocks,
    operationString,
    undefined,
    undefined,
    variableValues,
    operationName,
  );
  const body = {
    ...(result.data && { data: result.data }),
    ...(result.errors && { errors: result.errors }),
  };
  return {
    type: 'result',
    bodyText: JSON.stringify(body),
    body,
    headers: [{}],
    status: 200,
    size: 0,
    hasNext: false,
    queryPlan: undefined,
    startTime,
    endTime: startTime,
  };
};
